import camelCase from 'lodash/camelCase';
import set from 'lodash/set';
import snakeCase from 'lodash/snakeCase';

import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { MediaKindEnum } from '@/types/schema';

import { FilterBuilder } from '../builder/FilterBuilder';
import { Operator } from '../builder/types';
import { WidgetQueryResponse } from '../hooks/useWidgetQuery/types';

export const adaptEntity = (keyPrefix: string, entity: object) => {
  const _entity: any = {};

  Object.entries(entity).forEach(([key, value]) => {
    set(_entity, key, value);
  });

  return _entity[keyPrefix];
};

const GROUP_BY_KEY = 'groupBy.';

export const createFlatDocument = (data: any, entityPrefix: string) => {
  const adaptedData: any = {};

  for (const key in data) {
    if (key.includes(GROUP_BY_KEY)) {
      const flattenedData = (data[key] as any[]).map((entity) =>
        createFlatDocument(entity, entityPrefix),
      );
      const requiredKey = key.replace(GROUP_BY_KEY, '');
      adaptedData[requiredKey] = flattenedData;
    } else {
      adaptedData[key] = data[key];
    }
  }

  return adaptEntity(entityPrefix, adaptedData);
};

export const adaptReportData = (response?: WidgetQueryResponse): any[] | undefined => {
  if (!response) {
    return response;
  }

  const { query, data = { data: [] } } = response;
  return data.data.map((item: any) => adaptEntity(query.entity, item));
};

export const adaptEnumValue = <T = string>(value: string) => snakeCase(value).toUpperCase() as T;
export const adaptFromEnumValue = (value: string) => camelCase(value);

export const getFilterForValues = (key: string, values?: string[]) => {
  const filters =
    values?.map((value) =>
      new FilterBuilder().setOperator(Operator.EQUAL).setProperty(key).setValue(value),
    ) || [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getFilterForUserSearch = (value: string) => {
  const filters = value
    ? [
        new FilterBuilder()
          .setOperator(Operator.EQUAL)
          .setProperty('user.firstName.prefix')
          .setValue(value),
        new FilterBuilder()
          .setOperator(Operator.EQUAL)
          .setProperty('user.lastName.prefix')
          .setValue(value),
        new FilterBuilder()
          .setOperator(Operator.EQUAL)
          .setProperty('user.name.prefix')
          .setValue(value),
        new FilterBuilder().setOperator(Operator.EQUAL).setProperty('user.emailId').setValue(value),
      ]
    : [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getFilterForUserSearchText = (value: string) => {
  const filters = value
    ? [
        new FilterBuilder()
          .setOperator(Operator.IN)
          .setProperty('user.searchText')
          .setValue([value]),
      ]
    : [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getFilterForSessionSearch = (value: string) => {
  const filters = value
    ? [
        new FilterBuilder()
          .setOperator(Operator.IN)
          .setProperty('session.searchText')
          .setValue(value),
      ]
    : [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getFilterForContentSearch = (value: string) => {
  const filters = value
    ? [
        new FilterBuilder()
          .setOperator(Operator.IN)
          .setProperty('content.searchText')
          .setValue(value),
      ]
    : [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getFilterForFormSearch = (value: string) => {
  const filters = value
    ? [
        new FilterBuilder()
          .setOperator(Operator.EQUAL)
          .setProperty('firstName.prefix')
          .setValue(value),
        new FilterBuilder()
          .setOperator(Operator.EQUAL)
          .setProperty('lastName.prefix')
          .setValue(value),
        new FilterBuilder().setOperator(Operator.EQUAL).setProperty('name.prefix').setValue(value),
        new FilterBuilder().setOperator(Operator.EQUAL).setProperty('emailId').setValue(value),
      ]
    : [];

  return new FilterBuilder().setOperator(Operator.OR).addFilters(filters);
};

export const getUserIdsFilter = (userIds: string[]) =>
  new FilterBuilder().setOperator(Operator.IN).setProperty('userId').setValue(userIds);

export const adaptReportingMedia = (
  media: EntityMediaFragment & { kind: string },
): EntityMediaFragment => {
  return {
    ...media,
    kind: adaptEnumValue<MediaKindEnum>(media.kind),
  };
};

export const adaptReportingMedias = (
  medias: (EntityMediaFragment & { kind: string })[],
): EntityMediaFragment[] => {
  return medias.map((media) => adaptReportingMedia(media));
};
